import React, { useCallback } from 'react';
import styled from 'styled-components';

import { ORDER_STATUSES as STATUSES, TITLES } from './constants';
import { ValueOf } from '@utils/tsUtils';

interface IOrderStatus {
  className?: string;
  href?: string;
  id?: string;
  status: ValueOf<typeof STATUSES>;
  onClickId?: (id: this['id']) => void;
  suffix?: React.ReactNode;
}

const OrderStatus = React.memo(
  ({ className, href, id, status, onClickId, suffix }: IOrderStatus) => {
    const title = TITLES[status];

    const handleClickId = useCallback(() => {
      onClickId?.(id);
    }, [id, href, onClickId]);

    return (
      <StyledOrderStatus className={className} status={status} title={title}>
        {id && (
          <>
            №<StyledId onClick={handleClickId}>{id}</StyledId>
            {' · '}
          </>
        )}
        {title}
        {suffix}
      </StyledOrderStatus>
    );
  }
);

const StyledOrderStatus = styled.div<{ status: IOrderStatus['status'] }>`
  font-size: 14px;
  height: 23px;
  line-height: 23px;
  overflow: hidden;
  margin: 0 5px;
  padding: 0 7px;
  text-align: center;
  text-overflow: ellipsis;
  user-select: none;
  white-space: nowrap;

  ::first-letter {
    text-transform: uppercase;
  }

  ${({ status }) =>
    [STATUSES.APPROVED, STATUSES.COOLING].includes(status) &&
    `
    background-color: #f1c800;
    color: #fff;
  `}

  ${({ status }) =>
    status === STATUSES.BOOKED &&
    `
    background-color: #83550a;
    color: #fff;
  `}
  
  ${({ status }) =>
    status === STATUSES.CANCELED &&
    `
    background-color: #640000;
    color: #fff;
  `}

  ${({ status }) =>
    status === STATUSES.ANNULLED &&
    `
    background-color: #868686;
    color: #fff; 
  `}
  
  ${({ status }) =>
    status === STATUSES.CONFIRMED &&
    `
    background-color: #4a90e2;
    color: #fff;
  `}
  
  ${({ status }) =>
    [STATUSES.DEPOSIT, STATUSES.GOT_BACK, STATUSES.SURVEY_COMPLETED].includes(
      status
    ) &&
    `
    background-color: #fff;
    border: 1px solid #979797;
    color: #17181a;
    line-height: 21px;
  `}
  
  ${({ status }) =>
    status === STATUSES.DOCUMENTS_SENT &&
    `
    background-color: #69027e;
    color: #fff;
  `}
  
  ${({ status }) =>
    [STATUSES.FLIGHT_CONFIRMED, STATUSES.HOTEL_CONFIRMED].includes(status) &&
    `
    background-color: #30cb90;
    color: #fff;
  `}
  
  ${({ status }) =>
    status === STATUSES.CREATED &&
    `
    background-color: #fa6400;
    color: #fff;
  `}
  
  ${({ status }) =>
    status === STATUSES.NOT_CONFIRMED &&
    `
    background-color: #bc0000;
    color: #fff;
  `}
  
  ${({ status }) =>
    status === STATUSES.ON_TRIP &&
    `
    background-color: #2538df;
    color: #fff;
  `}
  
  ${({ status }) =>
    status === STATUSES.PAID &&
    `
    background-color: #00c02c;
    color: #fff;
  `}
`;

OrderStatus.displayName = 'OrderStatus';

const StyledId = styled.span`
  color: inherit;
  cursor: pointer;
  text-decoration: none;
  user-select: initial;

  :hover {
    text-decoration: underline;
  }
`;

export default OrderStatus;
